import { CategoriaBeneficio, Plan } from '../types/Plan';
import { FilaPrestacion, PlanConfig, EscenarioData, TramoEdad } from '../types/Calculadora';
import { PLANES } from './planes';
import { EXCLUSIVE_8080_BRACKETS, today } from '../components/dashboard/dashboardHelpers';

/**
 * Semilla de la calculadora de rentabilidad.
 *
 * Los precios salen del tablero que venía usando el negocio (Plan 8000/7000 v2)
 * y son un punto de partida, no una verdad: la idea es que se ajusten en
 * pantalla. La fila de internación arranca en cero porque no había un valor
 * promedio acordado — se carga a mano.
 */

/** Plan cuyo revenue se calcula por tramo etario y no por cuota plana. */
export const PLAN_POR_EDAD = 'Exclusive 8080';

export const TASA_USO_DEFAULT = 0.4;

interface FilaSemilla {
  id: string;
  nombre: string;
  categoria: CategoriaBeneficio;
  precioParticular: number;
  precioOsde: number;
  precioGappex: number;
  cantMin: number;
  cantMax: number;
}

/**
 * Prácticas base. TAC y RMN comparten la categoría `imagenes`, así que toman
 * el mismo % de descuento del plan aunque tengan precios muy distintos.
 */
const FILAS_SEMILLA: FilaSemilla[] = [
  { id: 'consulta', nombre: 'Consulta', categoria: 'consultas',
    precioParticular: 45000, precioOsde: 22244.8, precioGappex: 22244.8, cantMin: 5, cantMax: 10 },
  { id: 'tac', nombre: 'TAC', categoria: 'imagenes',
    precioParticular: 382000, precioOsde: 256000, precioGappex: 256000, cantMin: 1, cantMax: 1 },
  { id: 'rmn', nombre: 'RMN', categoria: 'imagenes',
    precioParticular: 249000, precioOsde: 158411.12, precioGappex: 120000, cantMin: 1, cantMax: 1 },
  { id: 'labo', nombre: 'Labo', categoria: 'laboratorio',
    precioParticular: 141859.82, precioOsde: 75781.16, precioGappex: 75781.16, cantMin: 2, cantMax: 3 },
  { id: 'guardia', nombre: 'Guardia', categoria: 'guardia',
    precioParticular: 59000, precioOsde: 30625.12, precioGappex: 30625.12, cantMin: 2, cantMax: 4 },
];

/**
 * Internación y cirugía entra como una sola línea con valor promedio, en vez de
 * abrirse en habitación, derechos quirúrgicos, honorarios y medicación. Toma el
 * % de `derechos-quirurgicos`, que es el núcleo del costo quirúrgico.
 */
const FILA_INTERNACION: FilaSemilla = {
  id: 'internacion', nombre: 'Internación y cirugía', categoria: 'derechos-quirurgicos',
  precioParticular: 0, precioOsde: 0, precioGappex: 0, cantMin: 0, cantMax: 1,
};

/** % de descuento que el plan da en esa categoría. Sin cobertura declarada, 0. */
export function descuentoDePlan(plan: Plan, categoria: CategoriaBeneficio | null): number {
  if (!categoria) return 0;
  const beneficio = plan.beneficios[categoria];
  if (!beneficio || beneficio.estado !== 'si' || beneficio.pct === null) return 0;
  return beneficio.pct;
}

function filaDesdeSemilla(semilla: FilaSemilla, plan: Plan, activa: boolean): FilaPrestacion {
  return {
    id: semilla.id,
    nombre: semilla.nombre,
    categoria: semilla.categoria,
    activa,
    modo: 'gappex',
    descuentoPct: descuentoDePlan(plan, semilla.categoria),
    precioParticular: semilla.precioParticular,
    precioOsde: semilla.precioOsde,
    precioGappex: semilla.precioGappex,
    // En modo gappex este valor no se usa; queda listo por si se invierte la fila.
    digitalPrestador: 0,
    cantMin: semilla.cantMin,
    cantMax: semilla.cantMax,
  };
}

/** Distribución etaria del 8080: los tramos reales, sin población asignada. */
export function tramosEdadVacios(): TramoEdad[] {
  return EXCLUSIVE_8080_BRACKETS.map((b) => ({
    label: `${b.min}-${b.max}`,
    valor: b.value,
    pct: 0,
  }));
}

export function filasPorDefecto(plan: Plan): FilaPrestacion[] {
  const filas = FILAS_SEMILLA.map((s) => filaDesdeSemilla(s, plan, true));
  // La línea de internación arranca encendida solo donde el plan la cubre:
  // en los demás no aporta nada y solo ensucia la tabla.
  const cubreInternacion = descuentoDePlan(plan, FILA_INTERNACION.categoria) > 0;
  filas.push(filaDesdeSemilla(FILA_INTERNACION, plan, cubreInternacion));
  return filas;
}

export function planConfigPorDefecto(plan: Plan): PlanConfig {
  const usaEdad = plan.nombre === PLAN_POR_EDAD;
  return {
    planId: plan.id,
    nombre: plan.nombre,
    incluido: true,
    suscriptores: 0,
    cuotaMensual: 0,
    tasaUso: TASA_USO_DEFAULT,
    usaDistribucionEdad: usaEdad,
    distribucionEdad: usaEdad ? tramosEdadVacios() : [],
    filas: filasPorDefecto(plan),
  };
}

/** Escenario en blanco: todos los planes vigentes, precios semilla, sin población. */
export function escenarioPorDefecto(): EscenarioData {
  return {
    version: 1,
    planes: PLANES.map(planConfigPorDefecto),
    fechaPrecios: today(), // fecha argentina, no UTC
  };
}

/**
 * Reconcilia un escenario guardado contra los planes vigentes: agrega los
 * planes nuevos y descarta los que ya no existen, sin tocar lo que el usuario
 * haya configurado en los que siguen vigentes.
 */
export function reconciliarEscenario(data: EscenarioData): EscenarioData {
  const guardados = new Map(data.planes.map((p) => [p.planId, p]));

  const vigentes = PLANES.map((plan) => {
    const guardado = guardados.get(plan.id);
    if (!guardado) return planConfigPorDefecto(plan);
    // El nombre puede haber cambiado en planes.ts; manda el dato vigente.
    return { ...guardado, nombre: plan.nombre };
  });

  // Los ficticios no están en el catálogo, así que no hay contra qué
  // reconciliarlos: se conservan tal cual, después de los vigentes.
  const ficticios = data.planes.filter((p) => p.ficticio);

  return {
    ...data,
    version: 1,
    planes: [...vigentes, ...ficticios],
  };
}

/** Nueva fila en blanco para agregar prácticas que no están en la semilla. */
export function filaEnBlanco(nombre: string): FilaPrestacion {
  return {
    id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    nombre,
    categoria: null,
    activa: true,
    modo: 'gappex',
    descuentoPct: 0,
    precioParticular: 0,
    precioOsde: 0,
    precioGappex: 0,
    digitalPrestador: 0,
    cantMin: 0,
    cantMax: 0,
  };
}

// ── Planes ficticios ───────────────────────────────────────────────────────

/** Prefijo de los ids generados a mano. Nunca choca con los del catálogo. */
const PREFIJO_FICTICIO = 'ficticio-';

function idFicticio(): string {
  return `${PREFIJO_FICTICIO}${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Prácticas de la semilla con sus precios pero sin descuento: el punto de
 * partida de un plan ficticio en blanco, donde la cobertura se define a mano.
 */
function filasSinCobertura(): FilaPrestacion[] {
  return [...FILAS_SEMILLA, FILA_INTERNACION].map((semilla) => ({
    id: semilla.id,
    nombre: semilla.nombre,
    categoria: semilla.categoria,
    // La internación arranca apagada: sin precio acordado no aporta nada.
    activa: semilla.id !== FILA_INTERNACION.id,
    modo: 'gappex' as const,
    descuentoPct: 0,
    precioParticular: semilla.precioParticular,
    precioOsde: semilla.precioOsde,
    precioGappex: semilla.precioGappex,
    digitalPrestador: 0,
    cantMin: semilla.cantMin,
    cantMax: semilla.cantMax,
  }));
}

/**
 * Plan inventado a mano. Se calcula exactamente igual que los del catálogo.
 *
 * Con `base` copia precios, cobertura y uso de ese plan; sin `base` arranca de
 * la semilla con 0% de descuento en todo. En los dos casos la población arranca
 * en cero: si heredara los suscriptores del plan base, el consolidado los
 * contaría dos veces.
 */
export function planFicticio(nombre: string, base?: PlanConfig): PlanConfig {
  if (!base) {
    return {
      planId: idFicticio(),
      nombre,
      ficticio: true,
      incluido: true,
      suscriptores: 0,
      cuotaMensual: 0,
      tasaUso: TASA_USO_DEFAULT,
      usaDistribucionEdad: false,
      distribucionEdad: [],
      filas: filasSinCobertura(),
    };
  }

  return {
    ...base,
    planId: idFicticio(),
    nombre,
    ficticio: true,
    incluido: true,
    suscriptores: 0,
    distribucionEdad: base.distribucionEdad.map((tramo) => ({ ...tramo })),
    filas: base.filas.map((fila) => ({ ...fila })),
  };
}
